<template>
  <NuxtLayout name="catalog">
    <div class="page-magazine">
      <div class="page-container">
        <MoleculesGlobalBreadcrumb :segments="[{ name: page.header, level: 0 }]" :show-all-segments="true"></MoleculesGlobalBreadcrumb>
      </div>

      <div class="bg-neutral-100 pt-6 pb-4">
        <div class="page-container">
          <h1 class="mb-6 text-4xl font-bold tracking-normal text-center">{{ page.header }}</h1>

          <!-- categories -->

          <div class="flex flex-wrap items-center gap-2">
            <span>{{ $t('Kategorie') }}:</span> <button v-for="category in entity.categories.filter(category => appConfig.magazineShownCategoriesIds.includes(category.id))" class="page-magazine__category" :class="{ 'page-magazine__category--selected': activeFilter.categories?.some(selectedCategory => category.id === selectedCategory.id) }" @click="filterApplied('c', category.id, false, false, true)">{{ category.name }}</button>
          </div>

          <div class="flex flex-wrap gap-x-4 gap-y-4 mt-4 p-4 bg-neutral-50 tablet:gap-x-6">

            <!-- labels buttons on mobile -->

            <button class="flex-shrink text-sm tracking-normal text-primary-500 underline tablet:hidden" @click="dialog.open('magazineLabels')">{{ $t('Štítky článků') }}</button>

            <!-- labels on tablet/desktop -->

            <div class="hidden flex-wrap items-center tablet:gap-2 tablet:flex desktop:flex desktop:justify-start desktop:flex-[2]">
              <strong class="mr-2 text-lg tracking-normal text-primary-500">{{ $t('Štítky') }}</strong>
              <button v-for="(label, index) in entity.infoPageFilter.filter(filterItem => filterItem.parameter.id === appConfig.parameterIds.magazineLabel).slice(0, 10)" class="page-magazine__label" :class="{ 'page-magazine__label--selected': activeFilter.labels?.some(selectedLabel => label.lValue === selectedLabel.lValue), '!mr-2': index === entity.infoPageFilter.length - 1 }" @click="filterApplied('l', label.lValue, false, false)">{{ label.value }}</button>
              <button class="font-bold underline text-primary-500" @click="dialog.open('magazineLabels')">{{ $t('Všechny štítky') }}</button>
            </div>

            <!-- text search -->

            <div class="page-magazine__search">
              <div class="flex-1 flex">
                <AtomsFormsTextInput type="search" v-model="searchText" class="flex-1 !border !bg-neutral-50 text-neutral-500 tablet:!pl-6" :placeholder="$t('Vyhledat článek')" ref="searchTextbox" @blur="closeSearchBox" @focus="openSearchBox" @enter="doSearch" @escape="closeSearchBox" />

                <div v-show="isSuggestOpened" class="page-magazine__search__suggest" :class="{ 'page-magazine__search__suggest--is-open': isSuggestOpened }" tabindex="-1" @focus="openSearchBox" @blur="closeSearchBox">
                  <div class="text-right">
                    <button @click="closeSearchBox" :title="i18n.t('Zavřít')">
                      <AtomsImagesIcon icon="cross" size="xs" class="-mr-2 p-1 !align-top"></AtomsImagesIcon>
                    </button>
                  </div>
                  <div v-if="numOfResults === 0 && searchText.length > 2" class="page-magazine__search__suggest__no-results" v-html="i18n.t('Pro zadaný dotaz jsme nenašli žádné výsledky.')"></div>
                  <div v-if="numOfResults > 0">

                    <!-- articles -->

                    <div v-if="searchResults?.magazines?.count" class="tracking-normal">
                      <AtomsTextLink v-for="article in searchResults.magazines.items" class="block mb-4 hover:no-underline" :href="article.page.url">
                        <div class="text-xl mb-1 font-bold">{{ article.page.title }}</div>
                        <div>{{ article.annotation }}</div>
                      </AtomsTextLink>
                    </div>

                    <!-- labels -->

                    <div v-if="searchResults?.infoPageFilters.count">
                      <div class="font-bold mb-4 tracking-normal text-primary-500">{{ $t('Štítky') }}</div>
                      <div class="flex flex-wrap items-center gap-2">
                        <button v-for="label in searchResults.infoPageFilters.items" class="page-magazine__label" :class="{ 'page-magazine__label--selected': activeFilter.labels?.some(selectedLabel => label.lValue === selectedLabel.lValue) }" @click="filterApplied('l', label.lValue, false, false)">{{ label.value }}</button>
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              <MoleculesButtonsButton :show-loader="suggestLoading" :secondary="true" class="block aspect-square w-14 p-0 bg-secondary-500 hover:bg-secondary-300" @click="doSearch">
                <AtomsImagesIcon icon="magnifying-glass" size="lg tablet:text-xl" />
              </MoleculesButtonsButton>

            </div>
          </div>

          <!-- active filter -->

          <div class="page-magazine__active-filter" v-show="activeFilter.searchText || activeFilter.labels.length || activeFilter.categories.length || activeFilter.sort">
            <strong class="w-full text-center text-primary-500 desktop:w-auto">{{ $t('Zobrazují se články:') }}</strong>

            <TransitionGroup name="list">

              <template v-if="activeFilter.searchText">
                <div class="flex flex-wrap items-center gap-2">
                  <strong class="uppercase text-neutral-300">{{ $t('Hledaný text') }}</strong>
                  <button class="page-magazine__label" @click="filterApplied('q', null, true, true)">{{ activeFilter.searchText }}
                    <AtomsImagesIcon icon="cross" class="ml-2 text-[0.5rem]" />
                  </button>
                </div>
              </template>

              <template v-if="activeFilter.categories.length">
                <div class="flex flex-wrap items-center gap-2 w-full tablet:justify-center desktop:w-auto">
                  <strong class="uppercase text-neutral-300">{{ $t('Kategorie magazínu') }}</strong>
                  <TransitionGroup>
                    <button v-for="category in activeFilter.categories" class="page-magazine__category" @click="filterApplied('c', category.id, false, false)">{{ category.name }}
                      <AtomsImagesIcon icon="cross" class="ml-2 text-[0.5rem]" />
                    </button>
                  </TransitionGroup>
                </div>
              </template>

              <template v-if="activeFilter.labels.length">
                <div class="flex flex-wrap items-center w-full tablet:gap-2 tablet:justify-center desktop:justify-start desktop:w-auto">
                  <strong class="mr-2 mb-2 uppercase text-neutral-300 tablet:mr-0 tablet:mb-0">{{ $t('Štítky') }}</strong>
                  <TransitionGroup>
                    <button v-for="label in activeFilter.labels" class="page-magazine__label" @click="filterApplied('l', label.lValue, false, false)">{{ label.value }}
                      <AtomsImagesIcon icon="cross" class="ml-2 text-[0.5rem]" />
                    </button>
                  </TransitionGroup>
                </div>
              </template>

              <template v-if="activeFilter.sort">
                <div class="flex flex-wrap items-center gap-2">
                  <strong class="uppercase text-neutral-300">{{ $t('Řazení') }}</strong>
                  <button class="page-magazine__label" @click="filterApplied('s', null, true, true)">{{ activeFilter.sort === 'new' ? $t('od nejnovějších') : $t('od nejčtenějších') }}
                    <AtomsImagesIcon icon="cross" class="ml-2 text-[0.5rem]" />
                  </button>
                </div>
              </template>

            </TransitionGroup>

            <button @click="clearFilter" class="font-bold text-error-500 tablet:mx-auto desktop:mr-0"><span class="underline">{{ $t('Zrušit filtraci') }}</span>
              <AtomsImagesIcon icon="cross" class="ml-1 text-[0.5rem]" />
            </button>
          </div>

        </div>
      </div>

      <div class="page-container" ref="articleList">
        <div v-show="filteredItems" class="pb-6">

          <template v-if="filteredItems?.length">
            <div class="my-6 grid gap-4 grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-3">
              <MoleculesBlocksMagazineItem v-for="article in filteredItems" :key="article.page.url" :with-button="true" :time-to-read="article.timeToRead" :date-published="article.page.publishedAt" :date-created="article.created" :title="article.page.title" title-element="h2" :annotation="article.annotation" :url="article.page.url" :item-id="article.id" class="min-w-[250px]"></MoleculesBlocksMagazineItem>
            </div>
            <OrganismsListsPaging class="mt-6" :total-pages="totalPages" :current-page="activeFilter.page" :total-items="filteredItemsCount" :auto-scroll-element="articleList"></OrganismsListsPaging>
          </template>

          <div v-else class="mt-4 py-24 text-2xl text-center text-neutral-300 border border-neutral-200">
            {{ $t('Pro zadaný filtr nebyly nalezeny žádné články.') }}
          </div>
        </div>

        <!-- default view without filtering applied -->

        <div v-show="!filteredItems" class="pb-6">

          <!-- newest articles -->

          <div class="flex items-center mt-6">
            <AtomsTextHeading class="ml-0 flex-1" text-align="left" text-class="!pl-0 tablet:!pl-4 text-left">{{ $t('Nejnovější za poslední měsíc') }}</AtomsTextHeading>
            <AtomsTextLink href="/rss-magazine.xml" target="_blank" class="text-[#DC6D38]">
              <AtomsImagesIcon icon="rss-full" size="4xl" />
            </AtomsTextLink>
          </div>

          <div class="my-6 flex flex-wrap gap-4">
            <MoleculesBlocksMagazineItem v-for="(article, index) in entity.newestArticles.infoPages.items" :with-button="true" :time-to-read="article.timeToRead" :date-published="article.page.publishedAt" :date-created="article.created" :title="article.page.title" title-element="h2" :annotation="article.annotation" :url="article.page.url" :item-id="article.id" class="min-w-[250px]" :class="{ '!hidden desktop:!flex': index > 1 }"></MoleculesBlocksMagazineItem>
          </div>

          <div class="desktop:text-right">
            <MoleculesButtonsButton :arrow-right="true" :secondary="true" @click="filterApplied('s', 'new', false, false, true)">{{ $t('Zobrazit všechny články od těch nejnovějších') }}</MoleculesButtonsButton>
          </div>

          <!-- most read articles -->

          <AtomsTextHeading class="mt-10" text-align="left" text-class="!pl-0 tablet:!pl-4 text-left">{{ $t('Nejčtenější články') }}</AtomsTextHeading>

          <div class="my-6 flex flex-wrap gap-4">
            <MoleculesBlocksMagazineItem v-for="(article, index) in entity.mostReadArticles.infoPages.items" :with-button="true" :time-to-read="article.timeToRead" :date-published="article.page.publishedAt" :date-created="article.created" :title="article.page.title" title-element="h2" :annotation="article.annotation" :url="article.page.url" :item-id="article.id" class="min-w-[250px]" :class="{ '!hidden desktop:!flex': index > 1 }"></MoleculesBlocksMagazineItem>
          </div>

          <div class="desktop:text-right">
            <MoleculesButtonsButton :arrow-right="true" :secondary="true" @click="filterApplied('s', 'mostread', false, false, true)">{{ $t('Zobrazit všechny články od těch nejčtenějších') }}</MoleculesButtonsButton>
          </div>

        </div>

        <!-- recently viewed items -->

        <LazyTemplatesRecentlyViewedItems class="mt-6" />

        <!-- usp -->

        <OrganismsBlocksUsps class="mb-4 desktop:mb-6" item-class="!bg-neutral-100 desktop:aspect-[7/3]" />
      </div>

      <OrganismsDialogsMagazineLabels :selected-labels="activeFilter.labels" @filter-applied="({ lValue, value }) => filterApplied('l', lValue, false, false)" />
    </div>

  </NuxtLayout>
</template>
<script setup>

import { useDebounceFn } from '@vueuse/core'

const appConfig = useAppConfig();
const entity = useEntity();
const page = usePage().value.page;
const cache = useCache();
const i18n = useI18n();
const locale = useLocale().getLocale();
const dialog = useDialog();
const shopRouting = useShopRouting();
const route = useRoute();

const articleList = ref(null);

const hash = ref(shopRouting.parseHash());
const isLoading = ref(false);

const filteredItems = ref(null);
const filteredItemsCount = ref(0);
const totalPages = computed(() => Math.ceil(filteredItemsCount.value / appConfig.magazineList.searchItems));

const activeFilter = computed(() => {
  return {
    searchText: hash.value.find(q => q.key === 'q')?.value?.[0] ?? '',
    labels: entity.infoPageFilter.filter(label => hash.value.find(q => q.key === 'l')?.value?.includes(label.lValue.toString())) ?? [],
    categories: entity.categories.filter(category => hash.value.find(q => q.key === 'c')?.value?.includes(category.id.toString())) ?? [],
    page: parseInt(hash.value.find(q => q.key === 'p')?.value?.[0]?.split('-')[0] ?? 1),
    append: hash.value.find(q => q.key === 'p')?.value?.[0]?.endsWith('-a'),
    sort: hash.value.find(q => q.key === 's')?.value?.[0],
  }
});

//#region search

const searchText = ref('');
const searchTextbox = ref(null);
const searchResults = ref({});
const suggestLoading = ref(false);
const isSuggestOpened = ref(false);

const numOfResults = computed(() => searchResults.value.magazines?.count);

let closeTimer;

const openSearchBox = () => {

  if (closeTimer) {
    clearTimeout(closeTimer);
  }

  if (searchText.value.length > 2) {
    isSuggestOpened.value = true;
  }
}

const closeSearchBox = () => {

  closeTimer = setTimeout(() => {
    isSuggestOpened.value = false;
  }, 100);
}

const suggest = useDebounceFn(async () => {
  if (searchText.value.length > 2) {
    suggestLoading.value = true;
    searchResults.value = (await useApiFetch(`/api/searchArticles?phrase=${encodeURIComponent(searchText.value)}&take=3&currencyId=${locale.currencyId}&languageId=${locale.languageId}`)).search;
    isSuggestOpened.value = true;
    suggestLoading.value = false;
  }
  else {
    isSuggestOpened.value = false;
  }
}, 200);

watch(() => searchText.value, (newValue, oldValue) => {

  if (newValue !== oldValue && newValue.length > 2) {
    suggest();
  }
});

const doSearch = () => {
  closeSearchBox();
  filterApplied('q', encodeURIComponent(searchText.value), false, true, true);
}

//#endregion

const updateArticleList = useDebounceFn(async () => {

  if (activeFilter.value.searchText || activeFilter.value.labels.length || activeFilter.value.categories.length || activeFilter.value.sort) {

    const updatedData = (await useApiFetch(`/api/magazineArticles?p=${activeFilter.value.page}&phrase=${activeFilter.value.searchText}&labelLValues=${activeFilter.value.labels.map(label => label.lValue).join(',')}&categoryIds=${activeFilter.value.categories.map(category => category.id).join(',')}&sort=${activeFilter.value.sort ?? 'new'}&take=${appConfig.magazineList.searchItems}&currencyId=${locale.currencyId}&languageId=${locale.languageId}`)).infoPageList.infoPages;

    if (activeFilter.value.append && filteredItems.value) {
      filteredItems.value = filteredItems.value.concat(updatedData.items);
    }
    else {
      filteredItems.value = updatedData.items;
    }

    filteredItemsCount.value = updatedData.count;
  }
  else {
    filteredItems.value = null;
    filteredItemsCount.value = 0;
  }

  isLoading.value = false;
}, 100);

// updates hash with selected filter values
const filterApplied = (urlParameter, urlValue, add, replaceValue, scrollToTop) => {

  // reset page to 1
  const modifiedHash = shopRouting.stringifyHash(shopRouting.updateValueInHash('p', '', false, true));

  // update filtered value in hash
  const hashObj = shopRouting.updateValueInHash(urlParameter, urlValue, add, replaceValue, modifiedHash);

  // navigate to the modified hash
  shopRouting.pushHash(hashObj);

  if (scrollToTop) {
    setTimeout(() => {
      useUtils().scrollToElement(articleList.value, 35);
    }, 200);
  }

}

const clearFilter = () => {
  useRouter().push({ hash: '' });
}

useSeoMeta({
  ogImage: `${useShopZone().value.staticResourcesRoot}/img/magazine/${entity.newestArticles.infoPages.items?.[0]?.id}.jpg`
});

// watch for changes in hash to update filter controls
watch(() => route.hash, (newValue, oldValue) => {
  if (!oldValue && activeFilter.value.labels?.length) {
    useUtils().scrollToElement(articleList.value);
  }

  isLoading.value = true;
  hash.value = shopRouting.parseHash();
  updateArticleList();
}, { flush: 'pre', immediate: route.hash });

</script>
<style lang="postcss">
.page-magazine {

  &__category {
    @apply inline-flex items-center tracking-normal underline;

    &::after {
      @apply content-[''] h-4 inline-block ml-2 border-r;
    }

    &:last-child::after {
      @apply hidden;
    }
  }

  &__search {
    @apply relative flex flex-1 tablet:p-3 tablet:bg-neutral-100;

    &__suggest {
      @apply absolute left-0 right-0 top-[100%] z-[31] p-4 max-h-[0] overflow-hidden border border-t-0 border-neutral-200 bg-neutral-50 text-neutral-500 opacity-0 tablet:p-6;

      &--is-open {

        @screen maxmobile {
          overflow-y: scroll;
          animation: slide-toggle-search-mobile 0.3s;
          animation-fill-mode: forwards;
        }

        @screen tablet {
          overflow-y: scroll;
          animation: slide-toggle-search-tablet 0.3s;
          animation-fill-mode: forwards;
        }

        @screen desktop {
          overflow-y: hidden;
          animation: slide-toggle 0.3s;
          animation-fill-mode: forwards;
        }
      }
    }
  }

  &__label {
    @apply inline-flex items-center px-4 py-2 mr-2 mb-2 text-sm uppercase text-center whitespace-nowrap text-primary-500 border border-primary-500 hover:no-underline tablet:mr-0 tablet:mb-0;

    &--selected {
      @apply bg-neutral-100 text-neutral-300 border-neutral-200;
    }
  }

  &__active-filter {
    @apply flex flex-wrap items-center gap-4 mt-4 p-4 tracking-normal border border-neutral-200 tablet:justify-center desktop:justify-start;

    .page-magazine__category {
      @apply inline-block no-underline px-4 py-2 tracking-normal bg-neutral-400 text-neutral-50 transition-all duration-300 hover:bg-neutral-500 hover:no-underline;

      &::after {
        @apply hidden;
      }

      &--selected {
        @apply tablet:bg-neutral-300;
      }
    }
  }

}
</style>